import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, GuildMemberResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildMembersReturn {
  members: GuildMemberResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
  updateRole: (userId: string, role: string) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
}

const LIMIT = 50;

export function useGuildMembers(guildId: string | undefined): UseGuildMembersReturn {
  const queryClient = useQueryClient();
  const membersQueryKey = queryKeys.guilds.members(guildId!);

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: membersQueryKey,
      queryFn: ({ pageParam = 0 }) =>
        api.getGuildMembers(guildId!, { limit: LIMIT, offset: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
      enabled: !!guildId,
    });

  const updateRoleMut = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      api.updateGuildMemberRole(guildId!, userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersQueryKey });
    },
  });

  const removeMemberMut = useMutation({
    mutationFn: (userId: string) => api.removeGuildMember(guildId!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersQueryKey });
    },
  });

  const members = data?.pages.flatMap((page) => page.members) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    members,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load members' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: !!hasNextPage,
    updateRole: async (userId: string, role: string) => {
      if (!guildId) return;
      await updateRoleMut.mutateAsync({ userId, role });
    },
    removeMember: async (userId: string) => {
      if (!guildId) return;
      await removeMemberMut.mutateAsync(userId);
    },
  };
}
